import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Phone, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useCountry } from '@/contexts/CountryContext';
import { COUNTRIES, CITIES, CATEGORIES } from '@/data/mockData';
import { Logo } from '@/components/Logo';
import { BRAND, CONTACT, SOCIAL } from '@/lib/brand';

// Tiktok icon (lucide doesn't ship a clean one)
const TiktokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M16 4v3.4a5.6 5.6 0 0 0 4 1.5v3a8.6 8.6 0 0 1-4-1v6.6a6.5 6.5 0 1 1-6.5-6.5c.3 0 .7 0 1 .1v3.1a3.4 3.4 0 1 0 2.4 3.3V4Z"/>
  </svg>
);

export const Footer = () => {
  const { country } = useCountry();
  const featuredCities = CITIES.filter((c) => c.country === country.code).slice(0, 6);

  return (
    <footer className="relative bg-[#0f1929] text-white pt-16 pb-8" data-testid="site-footer">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 pb-10 border-b border-white/10">
          {/* Brand column */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex items-center gap-2 mb-4" aria-label="InfoPoint Albania home">
              <Logo size={40} />
              <span className="font-heading font-bold text-lg">{BRAND.name}</span>
            </Link>
            <p className="text-sm text-white/70 mb-4">{BRAND.tagline} to the Heart of the Balkans</p>
            <p className="text-sm text-white/60 max-w-md leading-relaxed">
              {BRAND.description}
            </p>
            <div className="flex items-center gap-2 mt-5">
              <a href={SOCIAL.instagram} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00AA6C] flex items-center justify-center transition-colors">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={SOCIAL.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00AA6C] flex items-center justify-center transition-colors">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={SOCIAL.tiktok} target="_blank" rel="noopener noreferrer" aria-label="TikTok" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#00AA6C] flex items-center justify-center transition-colors">
                <TiktokIcon className="w-4 h-4" />
              </a>
              <a href={CONTACT.whatsappQuick('Hi InfoPoint Albania!')} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp" className="w-9 h-9 rounded-full bg-white/10 hover:bg-[#25D366] flex items-center justify-center transition-colors">
                <MessageCircle className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Explore */}
          <div className="lg:col-span-2">
            <h4 className="font-heading font-semibold mb-4">Explore</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li><Link to="/" className="hover:text-white">Home</Link></li>
              <li><Link to="/map" className="hover:text-white">Interactive Map</Link></li>
              <li><Link to="/pricing" className="hover:text-white">For Business · Pricing</Link></li>
              <li><Link to="/categories" className="hover:text-white">Categories</Link></li>
              <li><Link to="/search" className="hover:text-white">Search</Link></li>
              <li><Link to="/services" className="hover:text-white">Tours &amp; Services</Link></li>
              <li><Link to="/transit" className="hover:text-white">Transit</Link></li>
              <li><Link to="/ai" className="hover:text-white">AI Concierge</Link></li>
              <li><Link to="/about" className="hover:text-white">About Us</Link></li>
            </ul>
          </div>

          {/* Popular cities — internal SEO links */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold mb-4">Popular in {country.name}</h4>
            <ul className="space-y-2.5 text-sm text-white/75 grid grid-cols-2 gap-x-3">
              {featuredCities.map((c) => (
                <li key={c.slug}>
                  <Link to={`/${country.code}/${c.slug}`} className="hover:text-white">
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <h4 className="font-heading font-semibold mt-5 mb-2 text-sm">Top categories</h4>
            <ul className="text-sm text-white/65 grid grid-cols-2 gap-x-3 gap-y-1">
              {CATEGORIES.slice(0, 6).map((c) => (
                <li key={c.slug}>
                  <Link to={`/search?category=${c.slug}`} className="hover:text-white">{c.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-3">
            <h4 className="font-heading font-semibold mb-4">Contact</h4>
            <ul className="space-y-2.5 text-sm text-white/75">
              <li>
                <a href={CONTACT.whatsappQuick('Hi InfoPoint Albania!')} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 hover:text-white" data-testid="footer-whatsapp">
                  <MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp Us
                </a>
              </li>
              <li>
                <a href={CONTACT.telLink} className="flex items-center gap-2 hover:text-white" data-testid="footer-phone">
                  <Phone className="w-4 h-4" /> {CONTACT.phoneDisplay}
                </a>
              </li>
              <li>
                <Link to="/contact" className="flex items-center gap-2 hover:text-white">Contact form</Link>
              </li>
            </ul>
            <Button variant="green" size="sm" className="mt-4 w-full" asChild>
              <a href={CONTACT.whatsappQuick('Hi! I want to list my business on InfoPoint Albania.')} target="_blank" rel="noopener noreferrer">
                <MessageCircle className="w-4 h-4" /> WhatsApp Business
              </a>
            </Button>
            <Link to="/claim" className="block mt-3 text-sm text-[#D32323] hover:text-[#B01E1E] font-medium">
              Claim Your Business →
            </Link>
          </div>
        </div>

        {/* Country quick-links for SEO */}
        <div className="pt-6 pb-4 border-b border-white/10">
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-white/55">
            <span className="uppercase tracking-wider text-white/40">Countries:</span>
            {COUNTRIES.map((c) => (
              c.available ? (
                <Link key={c.code} to={`/${c.code}`} className="hover:text-white">
                  {c.flag} {c.name}
                </Link>
              ) : (
                <span key={c.code} className="text-white/30">{c.flag} {c.name}<span className="ml-1 text-[9px]">soon</span></span>
              )
            ))}
          </div>
        </div>

        <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-white/55">
          <span>© {new Date().getFullYear()} {BRAND.legalName}. All rights reserved.</span>
          <span>Premium tourism platform — Albania · Kosovo · North Macedonia</span>
        </div>
      </div>
    </footer>
  );
};
